import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { Popover } from '@/components/common'
import { eventGroup, humanizeLeague, humanizeSport } from './prepackSurfaceModel'

// Searchable event filter for event-partitioned surfaces. Events come from
// eventOptions() (grouped by league, earliest kickoff first); typing narrows
// on event label, team names, league and sport. Arrow keys move, Enter
// selects, Escape closes and returns focus to the trigger.

function formatEventStartTime(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

// `totalPackCount` is the surface's own count: a pack with legs in two games
// belongs to two events but is still one published assignment.
export default function PrepackEventPicker({ events = [], totalPackCount = 0, value = null, onChange }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const triggerRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const listId = useId()

  const selectedEvent = useMemo(() => events.find((e) => e.value === value) || null, [events, value])

  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return events
    return events.filter((e) => {
      if (e.label?.toLowerCase().includes(q)) return true
      if (e.league?.toLowerCase().includes(q)) return true
      const hLeague = humanizeLeague(e.league)
      if (hLeague?.toLowerCase().includes(q)) return true
      if (e.sport?.toLowerCase().includes(q)) return true
      const hSport = humanizeSport(e.sport)
      if (hSport?.toLowerCase().includes(q)) return true
      if (e.teamNames && e.teamNames.some((t) => t.toLowerCase().includes(q))) return true
      return false
    })
  }, [events, search])

  const groupedEvents = useMemo(() => {
    const map = new Map()
    for (const event of filteredEvents) {
      const group = eventGroup(event)
      if (!map.has(group)) map.set(group, [])
      map.get(group).push(event)
    }
    return [...map.entries()].map(([group, items]) => ({ group, items }))
  }, [filteredEvents])

  const { selectableItems, itemIndexMap } = useMemo(() => {
    const items = [{ type: 'all', value: null, label: 'All events' }]
    const indexMap = new Map()
    indexMap.set('__all__', 0)
    for (const group of groupedEvents) {
      for (const item of group.items) {
        const idx = items.length
        items.push({ type: 'event', ...item })
        indexMap.set(item.value, idx)
      }
    }
    return { selectableItems: items, itemIndexMap: indexMap }
  }, [groupedEvents])

  // A typed query puts the highlight on the first match, so Enter picks what
  // the operator is looking at rather than clearing the filter.
  useEffect(() => {
    setHighlightedIndex(search.trim() && selectableItems.length > 1 ? 1 : 0)
  }, [search, selectableItems.length])

  useEffect(() => {
    if (open) {
      setSearch('')
      setHighlightedIndex(0)
      const timer = window.setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
      return () => window.clearTimeout(timer)
    }
  }, [open])

  const selectItem = (item) => {
    onChange(item.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (selectableItems.length === 0) return
      setHighlightedIndex((prev) => (prev + 1) % selectableItems.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (selectableItems.length === 0) return
      setHighlightedIndex((prev) => (prev - 1 + selectableItems.length) % selectableItems.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectableItems[highlightedIndex]) {
        selectItem(selectableItems[highlightedIndex])
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    }
  }

  useEffect(() => {
    if (open && listRef.current && highlightedIndex >= 0) {
      const itemEl = listRef.current.querySelector(`[data-nav-index="${highlightedIndex}"]`)
      itemEl?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [open, highlightedIndex])

  const optionId = (index) => `${listId}-option-${index}`

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottom-start"
      label="Filter by event"
      className="w-80 max-w-[calc(100vw-2rem)] overflow-hidden"
      contentClassName="p-0 flex flex-col max-h-[22rem]"
      trigger={
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-label="Filter by event"
          aria-expanded={open}
          className="flex min-w-0 max-w-56 items-center justify-between gap-1.5 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2.5 py-1.5 text-left text-xs text-[var(--prepacks-text)] transition-colors hover:border-[var(--prepacks-muted)]"
        >
          <span className="truncate">{selectedEvent?.label || value || 'All events'}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />
        </button>
      }
    >
      <div className="flex items-center gap-1.5 border-b border-[var(--prepacks-border)] p-2">
        <Search className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-muted)]" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          role="searchbox"
          aria-label="Search events"
          aria-controls={listId}
          aria-activedescendant={selectableItems[highlightedIndex] ? optionId(highlightedIndex) : undefined}
          placeholder="Search events, teams, leagues…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          className="min-w-0 flex-1 bg-transparent text-xs text-[var(--prepacks-text)] placeholder-[var(--prepacks-muted)] outline-none"
        />
        {search ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => { setSearch(''); inputRef.current?.focus() }}
            className="p-0.5 text-[var(--prepacks-muted)] hover:text-[var(--prepacks-text)]"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div ref={listRef} id={listId} role="listbox" aria-label="Event options" className="flex-1 overflow-y-auto p-1 text-xs">
        <button
          type="button"
          id={optionId(0)}
          role="option"
          aria-selected={!value}
          data-nav-index={0}
          onClick={() => selectItem(selectableItems[0])}
          onMouseEnter={() => setHighlightedIndex(0)}
          className={`flex w-full items-center justify-between gap-2 rounded px-2.5 py-1.5 text-left text-xs transition-colors ${
            highlightedIndex === 0 ? 'bg-[var(--prepacks-raised)]' : ''
          } ${!value ? 'font-medium text-[var(--prepacks-accent-text)]' : 'text-[var(--prepacks-text)]'}`}
        >
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">All events</div>
            <div className="mt-0.5 text-[10px] text-[var(--prepacks-muted)]">
              {totalPackCount} {totalPackCount === 1 ? 'pack' : 'packs'}
            </div>
          </div>
          {!value ? <Check className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-accent-text)]" aria-hidden="true" /> : null}
        </button>

        {filteredEvents.length === 0 ? (
          <div className="p-3 text-center text-xs text-[var(--prepacks-muted)]">
            No events match "{search}"
          </div>
        ) : (
          <>
            <div className="my-1 border-t border-[var(--prepacks-border)]" />
            {groupedEvents.map((group) => (
              <div key={group.group} role="group" aria-label={group.group} className="mt-1 first:mt-0">
                <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--prepacks-muted)]">
                  {group.group}
                </div>
                {group.items.map((event) => {
                  const navIndex = itemIndexMap.get(event.value)
                  const isSelected = event.value === value
                  const isHighlighted = navIndex === highlightedIndex
                  return (
                    <button
                      key={event.value}
                      type="button"
                      id={optionId(navIndex)}
                      role="option"
                      aria-selected={isSelected}
                      data-nav-index={navIndex}
                      onClick={() => selectItem({ type: 'event', ...event })}
                      onMouseEnter={() => setHighlightedIndex(navIndex)}
                      className={`flex w-full items-center justify-between gap-2 rounded px-2.5 py-1.5 text-left text-xs transition-colors ${
                        isHighlighted ? 'bg-[var(--prepacks-raised)]' : ''
                      } ${isSelected ? 'font-medium text-[var(--prepacks-accent-text)]' : 'text-[var(--prepacks-text)]'}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{event.label}</div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-[var(--prepacks-muted)]">
                          {event.startTime ? <span>{formatEventStartTime(event.startTime)}</span> : null}
                          {event.startTime ? <span>·</span> : null}
                          <span>{event.count} {event.count === 1 ? 'pack' : 'packs'}</span>
                        </div>
                      </div>
                      {isSelected ? <Check className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-accent-text)]" aria-hidden="true" /> : null}
                    </button>
                  )
                })}
              </div>
            ))}
          </>
        )}
      </div>
    </Popover>
  )
}
